import { parseJsonLenient } from '@/shared/utils/helpers';

/**
 * True when `schema` is a JSON Schema whose root type is `object`.
 * OpenAI Structured Outputs reject primitives such as `{"type": "string"}`.
 */
export function isObjectJsonSchema(schema) {
    return Boolean(
        schema
        && typeof schema === 'object'
        && !Array.isArray(schema)
        && schema.type === 'object'
    );
}

/**
 * Infer a JSON Schema (draft-agnostic subset) from a parsed JSON value.
 * Objects contribute `properties` + `required`; arrays infer `items` from
 * their first element. Primitives map to their JSON-schema type.
 */
export function inferJsonSchemaFromValue(value) {
    if (value === null) {
        return { type: 'null' };
    }
    if (Array.isArray(value)) {
        return {
            type: 'array',
            ...(value.length > 0 ? { items: inferJsonSchemaFromValue(value[0]) } : {}),
        };
    }
    switch (typeof value) {
        case 'string':
            return { type: 'string' };
        case 'boolean':
            return { type: 'boolean' };
        case 'number':
            return { type: Number.isInteger(value) ? 'integer' : 'number' };
        case 'object': {
            const properties = {};
            Object.keys(value).forEach((key) => {
                properties[key] = inferJsonSchemaFromValue(value[key]);
            });
            return {
                type: 'object',
                properties,
                required: Object.keys(value),
            };
        }
        default:
            return {};
    }
}

/**
 * Infer a pretty-printed JSON Schema string from a raw LLM response.
 * Handles markdown code fences and lenient JSON. Only object values produce
 * a schema — primitives and arrays cannot be used with structured output.
 */
export function inferJsonSchemaFromResponse(rawResponse) {
    if (rawResponse == null || typeof rawResponse !== 'string') return null;
    if (!rawResponse.trim()) return null;
    let parsed;
    try {
        parsed = parseJsonLenient(rawResponse);
    } catch {
        return null;
    }
    if (parsed === undefined) {
        return null;
    }
    const schema = inferJsonSchemaFromValue(parsed);
    if (!isObjectJsonSchema(schema)) {
        return null;
    }
    return JSON.stringify(schema, null, 2);
}

export const SCHEMA_INFER_NEEDS_OBJECT_MESSAGE =
    'Could not infer a schema — structured output requires a JSON object response, not plain text.';

/**
 * "Infer from response" is available once a teacher model and user prompt
 * are set. Model Returns is filled on click when it is still empty.
 */
export function canInferJsonSchemaFromTeacherSetup({ teacher, prompt } = {}) {
    return Boolean(teacher) && Boolean((prompt || '').trim());
}

/** Returns an error string when `schemaText` is non-empty invalid JSON, else null. */
export function getJsonSchemaError(schemaText) {
    const trimmed = (schemaText || '').trim();
    if (!trimmed) return null;
    try {
        const parsed = JSON.parse(trimmed);
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
            return 'Schema must be a JSON object';
        }
        if (parsed.type !== 'object') {
            return 'Schema must have type "object" - structured output cannot use a string, array, or other root type';
        }
        return null;
    } catch (e) {
        return `Invalid JSON: ${e.message}`;
    }
}

/**
 * Error for an *enabled* JSON schema field (non-empty teacher schema, or a
 * non-empty student override). Empty / inherited / toggle-off fields are
 * skipped — structured output is simply not used.
 *
 * @returns {{source: 'teacher'|'student', message: string}|null}
 */
export function getEnabledJsonSchemaError(usecase) {
    const teacherError = getJsonSchemaError(usecase?.teacher_schema);
    if (teacherError) {
        return { source: 'teacher', message: teacherError };
    }
    if (usecase?.generative_schema != null) {
        const studentError = getJsonSchemaError(usecase.generative_schema);
        if (studentError) {
            return { source: 'student', message: studentError };
        }
    }
    return null;
}

/** True when two schema texts represent the same JSON value (ignores whitespace). */
export function schemasEquivalent(a, b) {
    const left = (a || '').trim();
    const right = (b || '').trim();
    if (left === right) return true;
    if (!left || !right) return false;
    try {
        return JSON.stringify(JSON.parse(left)) === JSON.stringify(JSON.parse(right));
    } catch {
        return false;
    }
}
